import { Permission } from "@/permissions";
import { NotificationRegistryRead } from "@/services/generatedApi";
import { isAfter, subDays } from "date-fns";
import { z } from "zod";

const commentLocationSchema = z.object({
  assessment_id: z.string(),
  site_assessment_id: z.string(),
  task_id: z.string(),
});

function buildCommentUrl(location: unknown, permissions: Permission[]): string | undefined {
  const result = commentLocationSchema.safeParse(location);
  if (result.error) {
    console.error("Invalid location:", result.error);
    return;
  }

  const { assessment_id, site_assessment_id, task_id } = result.data;
  const baseUrl = `/assessments/${assessment_id}/${site_assessment_id}/${task_id}`;
  return permissions.includes("reviewer") ? `${baseUrl}/review` : baseUrl;
}

const reasonSchema = z.enum(["Comment"]).optional().nullable();
/**
 * Since we don't have proper serializer for notification body, we need to contents manually.
 */
export function buildNotificationLink(
  { notification_body: { hyperlink, reason, location } }: NotificationRegistryRead,
  permissions: Permission[],
): string | undefined {
  const defaultLink = hyperlink || undefined;

  const result = reasonSchema.safeParse(reason);
  if (result.error) {
    console.error("Invalid reason:", result.error);
    return defaultLink;
  }

  if (result.data === "Comment") {
    return buildCommentUrl(location, permissions) ?? defaultLink;
  }

  return defaultLink;
}

export const groupByDate = (list: NotificationRegistryRead[]) => {
  const today: NotificationRegistryRead[] = [];
  const yesterday: NotificationRegistryRead[] = [];
  const lastWeek: NotificationRegistryRead[] = [];
  const older: NotificationRegistryRead[] = [];

  list.forEach(notification => {
    const d = new Date(notification.notification_body.created_at);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    if (isAfter(d, todayDate)) {
      today.push(notification);
    } else if (isAfter(d, subDays(todayDate, 1))) {
      yesterday.push(notification);
    } else if (isAfter(d, subDays(todayDate, 7))) {
      lastWeek.push(notification);
    } else {
      older.push(notification);
    }
  });
  return {
    today,
    yesterday,
    lastWeek,
    older,
  };
};
